import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Ticket, Search, RefreshCw, Mail, X, CheckCircle2, Circle, AlertCircle, Clock, Users,
  Layers, ChevronRight,
} from "lucide-react";

const STATUS_TABS = [
  { v: "all",       l: "Alle" },
  { v: "unused",    l: "Nicht abgeholt" },
  { v: "partial",   l: "Teilweise" },
  { v: "complete",  l: "Vollständig" },
  { v: "no_shows",  l: "Vergangene ohne Nutzung" },
];

const fmtDate = (iso) => {
  if (!iso) return "";
  const [d, ...rest] = iso.split("T");
  const parts = d.split("-");
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}${rest[0] ? " · " + rest[0].slice(0, 5) : ""}`;
  return iso;
};

const isPast = (dateStr) => {
  if (!dateStr) return false;
  return dateStr < new Date().toISOString().slice(0, 10);
};

export default function MarathonAdmin() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/marathon/tickets");
      setRows(data);
    } catch { toast.error("Konnte Marathon-Tickets nicht laden"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = rows;
    if (tab === "unused") arr = arr.filter((r) => (r.scan_count || 0) === 0);
    else if (tab === "partial") arr = arr.filter((r) => (r.scan_count || 0) > 0 && (r.scan_count || 0) < (r.show_count || 0));
    else if (tab === "complete") arr = arr.filter((r) => (r.scan_count || 0) >= (r.show_count || 0) && (r.show_count || 0) > 0);
    else if (tab === "no_shows") arr = arr.filter((r) => {
      if ((r.scan_count || 0) > 0) return false;
      // alle Vorstellungen bereits vorbei
      const allPast = (r.showtimes || []).every((s) => isPast(s.date));
      return allPast && (r.showtimes || []).length > 0;
    });
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter((r) => (r.code || "").toLowerCase().includes(s) || (r.guest_name || "").toLowerCase().includes(s) || (r.email || "").toLowerCase().includes(s) || (r.series_name || "").toLowerCase().includes(s));
    }
    return arr;
  }, [rows, tab, q]);

  const resend = async (code) => {
    try {
      await api.post(`/admin/marathon/${code}/resend`);
      toast.success("E-Mail wird erneut gesendet");
    } catch { toast.error("Versand fehlgeschlagen"); }
  };

  const counts = useMemo(() => ({
    all: rows.length,
    unused: rows.filter((r) => (r.scan_count || 0) === 0).length,
    partial: rows.filter((r) => (r.scan_count || 0) > 0 && (r.scan_count || 0) < (r.show_count || 0)).length,
    complete: rows.filter((r) => (r.scan_count || 0) >= (r.show_count || 0) && (r.show_count || 0) > 0).length,
    no_shows: rows.filter((r) => (r.scan_count || 0) === 0 && (r.showtimes || []).length > 0 && (r.showtimes || []).every((s) => isPast(s.date))).length,
  }), [rows]);

  return (
    <div className="space-y-6" data-testid="marathon-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Ticket size={22} className="text-[#D4AF37]" />
          <div>
            <h1 className="font-heading text-3xl">Marathon-Buchungen</h1>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-1">Bezahlt vs. Erschienen · Scan-History · E-Mail erneut senden</div>
          </div>
        </div>
        <button onClick={load} data-testid="marathon-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest hover:border-[#D4AF37]">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Aktualisieren
        </button>
      </div>

      <div className="flex flex-wrap gap-2 items-center" data-testid="marathon-tabs">
        {STATUS_TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`marathon-tab-${t.v}`}
            className={`px-3 py-1.5 rounded text-[10px] uppercase tracking-widest border transitions-safe ${
              tab === t.v ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/60"
            }`}>
            {t.l} <span className="text-[#575148]">· {counts[t.v]}</span>
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#575148]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="marathon-search"
            placeholder="Code · Name · E-Mail · Reihe" className="bg-[#050505] border border-[#2A2A2A] rounded pl-8 pr-3 py-1.5 text-sm w-64 focus:border-[#D4AF37] outline-none" />
        </div>
      </div>

      <div className="card-lux overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#1A1A1A] text-[10px] uppercase tracking-widest text-[#575148]">
              <th className="text-left px-4 py-3">Datum</th>
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-left px-4 py-3">Gast</th>
              <th className="text-left px-4 py-3">Filmreihe</th>
              <th className="text-right px-4 py-3">Sitze</th>
              <th className="text-right px-4 py-3">Preis</th>
              <th className="text-center px-4 py-3">Scans</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Aktion</th>
            </tr>
          </thead>
          <tbody data-testid="marathon-rows">
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-16 text-[#575148]">
                <Ticket size={32} className="mx-auto mb-3 opacity-30" />
                Keine Buchungen{tab !== "all" ? " in diesem Filter" : ""}.
              </td></tr>
            )}
            {filtered.map((r) => {
              const scanCount = r.scan_count || 0;
              const showCount = r.show_count || 0;
              const complete = scanCount >= showCount && showCount > 0;
              const partial = scanCount > 0 && !complete;
              const allPast = (r.showtimes || []).length > 0 && (r.showtimes || []).every((s) => isPast(s.date));
              const noShow = scanCount === 0 && allPast;
              return (
                <tr key={r.id} onClick={() => setDetail(r)} data-testid={`marathon-row-${r.id}`}
                  className="border-b border-[#1A1A1A] hover:bg-[#D4AF37]/[0.03] cursor-pointer">
                  <td className="px-4 py-3 text-[#A8A398] text-xs font-mono">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3 font-mono text-[#EEDC82] text-xs">{r.code}</td>
                  <td className="px-4 py-3">
                    <div className="text-[#F5F0E6]">{r.guest_name}</div>
                    <div className="text-[10px] text-[#575148]">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 text-[#A8A398]">{r.series_name}</td>
                  <td className="px-4 py-3 text-right text-[#F5F0E6] font-mono">{r.seat_count || 1}</td>
                  <td className="px-4 py-3 text-right text-[#D4AF37] font-heading">{Number(r.price || 0).toFixed(2)} €</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 font-mono text-xs px-2 py-0.5 rounded ${
                      complete ? "bg-[#8FBC8F]/15 text-[#B7E1B7]" :
                      partial  ? "bg-[#D4AF37]/15 text-[#EEDC82]" :
                                 "bg-[#575148]/15 text-[#575148]"
                    }`}>
                      {scanCount}/{showCount}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {complete ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#B7E1B7]"><CheckCircle2 size={11} /> Komplett</span>
                    : partial ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#EEDC82]"><Circle size={11} /> Teilweise</span>
                    : noShow  ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#EE9CA1]"><AlertCircle size={11} /> No-Show</span>
                              : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#575148]"><Clock size={11} /> Offen</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); resend(r.code); }} data-testid={`marathon-resend-${r.code}`}
                      className="inline-flex items-center gap-1 px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] text-[10px] uppercase tracking-widest text-[#A8A398] hover:text-[#D4AF37]">
                      <Mail size={11} /> Erneut senden
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detail && <DetailDialog t={detail} onClose={() => setDetail(null)} onResend={resend} />}
    </div>
  );
}

function DetailDialog({ t, onClose, onResend }) {
  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-2xl bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl my-4 sm:my-8" onClick={(e) => e.stopPropagation()} data-testid="marathon-detail">
        <div className="p-5 border-b border-[#1A1A1A] flex items-center gap-3">
          <Layers size={20} className="text-[#D4AF37]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Marathon-Buchung</div>
            <div className="font-heading text-xl text-[#F5F0E6]">{t.series_name}</div>
          </div>
          <button onClick={onClose} className="ml-auto"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="text-[9px] uppercase tracking-widest text-[#575148]">Code</div><div className="font-mono text-[#EEDC82] mt-1">{t.code}</div></div>
            <div><div className="text-[9px] uppercase tracking-widest text-[#575148]">Gast</div><div className="text-[#F5F0E6] mt-1">{t.guest_name}</div></div>
            <div><div className="text-[9px] uppercase tracking-widest text-[#575148]">Sitze</div><div className="text-[#F5F0E6] mt-1 font-mono">{t.seat_count}</div></div>
            <div><div className="text-[9px] uppercase tracking-widest text-[#575148]">Preis</div><div className="text-[#D4AF37] mt-1 font-heading">{Number(t.price).toFixed(2)} €</div></div>
            <div className="col-span-2"><div className="text-[9px] uppercase tracking-widest text-[#575148]">E-Mail</div><div className="text-[#A8A398] mt-1 text-xs font-mono">{t.email}</div></div>
            {t.phone && <div className="col-span-2"><div className="text-[9px] uppercase tracking-widest text-[#575148]">Telefon</div><div className="text-[#A8A398] mt-1 text-xs font-mono">{t.phone}</div></div>}
            {t.note && <div className="col-span-4"><div className="text-[9px] uppercase tracking-widest text-[#575148]">Notiz</div><div className="text-[#A8A398] mt-1 text-xs italic">{t.note}</div></div>}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-2 flex items-center gap-2">
              <Users size={12} /> Vorstellungen · {t.scan_count || 0}/{t.show_count || 0} eingelöst
            </div>
            <div className="space-y-1" data-testid="marathon-detail-shows">
              {(t.showtimes || []).map((s) => {
                const scanned = (t.scans || []).find((sc) => sc.showtime_id === s.id);
                return (
                  <div key={s.id} className={`flex items-center gap-3 p-2.5 bg-[#050505] border rounded text-xs ${scanned ? "border-[#8FBC8F]/40" : "border-[#1A1A1A]"}`}>
                    {s.movie?.poster_url && <img src={s.movie.poster_url} alt="" className="w-8 h-11 object-cover rounded" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-[#F5F0E6]">{s.movie?.title_de || "Film"}</div>
                      <div className="font-mono text-[10px] text-[#A8A398] mt-0.5">{(s.date || "").split("-").reverse().join(".")} · {s.start_time} · {s.hall}</div>
                    </div>
                    {scanned ? (
                      <div className="text-right">
                        <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#B7E1B7]"><CheckCircle2 size={11} /> Eingelassen</div>
                        <div className="text-[9px] font-mono text-[#575148]">{fmtDate(scanned.at)}</div>
                      </div>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest text-[#575148]">Offen</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center px-5 py-4 border-t border-[#1A1A1A]">
          <div className="text-[10px] uppercase tracking-widest text-[#575148]">Erstellt: {fmtDate(t.created_at)}</div>
          <div className="flex gap-2">
            <button onClick={() => onResend(t.code)} data-testid="marathon-detail-resend"
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#D4AF37]/50 text-[#D4AF37] rounded text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10">
              <Mail size={11} /> QR erneut senden
            </button>
            <button onClick={onClose} className="px-4 py-1.5 border border-[#2A2A2A] rounded text-[10px] uppercase tracking-widest">Schließen</button>
          </div>
        </div>
      </div>
    </div>
  );
}
